// src/lib/cat/spellchecker.ts
/**
 * Spellchecker using LanguageTool API
 * 
 * FREE API: https://languagetool.org/http-api/
 * Rate Limit: 20 requests/minute (free tier)
 * Supports: 30+ languages
 * 
 * Features:
 * - Spelling errors
 * - Grammar errors
 * - Style suggestions
 * - Real-time checking
 * - Ignore list support
 */

export interface SpellingError {
  message: string
  shortMessage: string
  offset: number
  length: number
  category: 'TYPOS' | 'GRAMMAR' | 'STYLE' | 'MISC'
  type: string
  replacements: string[]
  sentence: string
  context: {
    text: string
    offset: number
    length: number
  }
  rule: {
    id: string
    description: string
    category: string
  }
}

export interface SpellCheckResult {
  errors: SpellingError[]
  language: string
  checkedText: string
}

export interface SpellCheckerOptions {
  language: string
  enabledRules?: string[]
  disabledRules?: string[]
  enabledCategories?: string[]
  disabledCategories?: string[]
  ignoreWords?: string[]
}

class SpellChecker {
  private apiUrl = 'https://api.languagetool.org/v2/check'
  private rateLimitDelay = 3000 // 3 seconds between requests (conservative)
  private lastRequestTime = 0
  private ignoredWords: Set<string> = new Set()

  constructor() {
    // Load ignored words from localStorage
    this.loadIgnoredWords()
  }

  /**
   * Check text for spelling and grammar errors
   */
  async checkText(
    text: string,
    options: SpellCheckerOptions
  ): Promise<SpellCheckResult> {
    // Rate limiting
    await this.enforceRateLimit()

    // Prepare request
    const formData = new URLSearchParams()
    formData.append('text', text)
    formData.append('language', this.mapLanguageCode(options.language))

    // Add enabled/disabled rules
    if (options.enabledRules && options.enabledRules.length > 0) {
      formData.append('enabledRules', options.enabledRules.join(','))
    }
    if (options.disabledRules && options.disabledRules.length > 0) {
      formData.append('disabledRules', options.disabledRules.join(','))
    }

    // Add enabled/disabled categories
    if (options.enabledCategories && options.enabledCategories.length > 0) {
      formData.append('enabledCategories', options.enabledCategories.join(','))
    }
    if (options.disabledCategories && options.disabledCategories.length > 0) {
      formData.append('disabledCategories', options.disabledCategories.join(','))
    }

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': 'application/json',
        },
        body: formData.toString(),
      })

      if (!response.ok) {
        throw new Error(`LanguageTool API error: ${response.status}`)
      }

      const data = await response.json()

      // Filter out ignored words
      const filteredMatches = data.matches.filter((match: any) => {
        const word = text.substring(match.offset, match.offset + match.length)
        return !this.ignoredWords.has(word.toLowerCase())
      })

      // Map to our error format
      const errors: SpellingError[] = filteredMatches.map((match: any) => ({
        message: match.message,
        shortMessage: match.shortMessage || match.message,
        offset: match.offset,
        length: match.length,
        category: this.categorizeError(match.rule.category.id),
        type: match.rule.issueType,
        replacements: match.replacements
          .slice(0, 5) // Limit to 5 suggestions
          .map((r: any) => r.value),
        sentence: match.sentence,
        context: {
          text: match.context.text,
          offset: match.context.offset,
          length: match.context.length,
        },
        rule: {
          id: match.rule.id,
          description: match.rule.description,
          category: match.rule.category.name,
        },
      }))

      return {
        errors,
        language: options.language,
        checkedText: text,
      }
    } catch (error) {
      console.error('[SpellChecker] Error checking text:', error)
      throw error
    }
  }

  /**
   * Check multiple segments in batch (with rate limiting)
   */
  async checkSegments(
    segments: Array<{ id: string; text: string }>,
    language: string,
    onProgress?: (current: number, total: number) => void
  ): Promise<Map<string, SpellCheckResult>> {
    const results = new Map<string, SpellCheckResult>()

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i]
      
      if (segment.text.trim()) {
        try {
          const result = await this.checkText(segment.text, { language })
          results.set(segment.id, result)
        } catch (error) {
          console.error(`[SpellChecker] Error checking segment ${segment.id}:`, error)
        }
      }

      // Report progress
      if (onProgress) {
        onProgress(i + 1, segments.length)
      }
    }

    return results
  }

  /**
   * Add word to ignore list
   */
  addToIgnoreList(word: string): void {
    this.ignoredWords.add(word.toLowerCase())
    this.saveIgnoredWords()
  }

  /**
   * Remove word from ignore list
   */
  removeFromIgnoreList(word: string): void {
    this.ignoredWords.delete(word.toLowerCase())
    this.saveIgnoredWords()
  }

  /**
   * Get ignore list
   */
  getIgnoreList(): string[] {
    return Array.from(this.ignoredWords)
  }

  /**
   * Clear ignore list
   */
  clearIgnoreList(): void {
    this.ignoredWords.clear()
    this.saveIgnoredWords()
  }

  /**
   * Get supported languages
   */
  getSupportedLanguages(): Array<{ code: string; name: string }> {
    return [
      { code: 'en', name: 'English' },
      { code: 'en-US', name: 'English (US)' },
      { code: 'en-GB', name: 'English (UK)' },
      { code: 'de', name: 'German' },
      { code: 'de-DE', name: 'German (Germany)' },
      { code: 'de-AT', name: 'German (Austria)' },
      { code: 'de-CH', name: 'German (Switzerland)' },
      { code: 'fr', name: 'French' },
      { code: 'es', name: 'Spanish' },
      { code: 'pt', name: 'Portuguese' },
      { code: 'pt-BR', name: 'Portuguese (Brazil)' },
      { code: 'pt-PT', name: 'Portuguese (Portugal)' },
      { code: 'it', name: 'Italian' },
      { code: 'nl', name: 'Dutch' },
      { code: 'pl', name: 'Polish' },
      { code: 'ru', name: 'Russian' },
      { code: 'uk', name: 'Ukrainian' },
      { code: 'zh', name: 'Chinese' },
      { code: 'ja', name: 'Japanese' },
      { code: 'ar', name: 'Arabic' },
      { code: 'ca', name: 'Catalan' },
      { code: 'da', name: 'Danish' },
      { code: 'el', name: 'Greek' },
      { code: 'fa', name: 'Persian' },
      { code: 'ro', name: 'Romanian' },
      { code: 'sk', name: 'Slovak' },
      { code: 'sl', name: 'Slovenian' },
      { code: 'sv', name: 'Swedish' },
      { code: 'ta', name: 'Tamil' },
      { code: 'tl', name: 'Tagalog' },
    ]
  }

  // Private methods

  private async enforceRateLimit(): Promise<void> {
    const now = Date.now()
    const timeSinceLastRequest = now - this.lastRequestTime

    if (timeSinceLastRequest < this.rateLimitDelay) {
      const waitTime = this.rateLimitDelay - timeSinceLastRequest
      await new Promise(resolve => setTimeout(resolve, waitTime))
    }

    this.lastRequestTime = Date.now()
  }

  private mapLanguageCode(code: string): string {
    // Map common language codes to LanguageTool format
    const mapping: Record<string, string> = {
      'en': 'en-US',
      'pt': 'pt-PT',       // Default to European Portuguese
      'pt-br': 'pt-BR',
      'pt-pt': 'pt-PT',
      'de': 'de-DE',
      'es': 'es',
      'fr': 'fr',
      'it': 'it',
      'nl': 'nl',
      'pl': 'pl',
      'ru': 'ru',
      'zh': 'zh-CN',
      'ja': 'ja-JP',
    }

    return mapping[code.toLowerCase()] || code
  }

  private categorizeError(categoryId: string): SpellingError['category'] {
    if (categoryId.includes('TYPO')) return 'TYPOS'
    if (categoryId.includes('GRAMMAR')) return 'GRAMMAR'
    if (categoryId.includes('STYLE')) return 'STYLE'
    return 'MISC'
  }

  private loadIgnoredWords(): void {
    try {
      const stored = localStorage.getItem('translyxcat_ignored_words')
      if (stored) {
        const words = JSON.parse(stored) as string[]
        this.ignoredWords = new Set(words.map(w => w.toLowerCase()))
      }
    } catch (error) {
      console.error('[SpellChecker] Error loading ignored words:', error)
    }
  }

  private saveIgnoredWords(): void {
    try {
      const words = Array.from(this.ignoredWords)
      localStorage.setItem('translyxcat_ignored_words', JSON.stringify(words))
    } catch (error) {
      console.error('[SpellChecker] Error saving ignored words:', error)
    }
  }
}

// Singleton instance
export const spellChecker = new SpellChecker()

// Debounced spell check for real-time checking
let spellCheckTimeout: NodeJS.Timeout | null = null

export function debouncedSpellCheck(
  text: string,
  language: string,
  delay: number = 2000
): Promise<SpellCheckResult> {
  return new Promise((resolve, reject) => {
    if (spellCheckTimeout) {
      clearTimeout(spellCheckTimeout)
    }

    spellCheckTimeout = setTimeout(async () => {
      try {
        const result = await spellChecker.checkText(text, { language })
        resolve(result)
      } catch (error) {
        reject(error)
      }
    }, delay)
  })
}